"use client";

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { resolveGrowProfile } from "./grow-card-carousel.profiles";
import {
  distanceFromCentre,
  easeGlide,
  measureRail,
  measuredSeekTarget,
  nearestSlot,
  seekTarget,
  slotAt,
  type RailMetrics
} from "./rail-geometry";

const useIsomorphicLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect;

const DRAG_THRESHOLD_PX = 5;

export const REPEATS = 3;

const GLIDE_MIN_MS = 460;
const GLIDE_MAX_MS = 900;

/* How long the rail waits after a gesture stops before landing on a card. */
const SETTLE_MS = 220;

/* A self-started seek's time-to-live, in case `scrollend` never fires. */
const SEEK_MS = 1400;

/* Clearance kept between a brought-into-view tab and the scroller's edge. */
const TAB_GUTTER_PX = 16;

export type RailScroll = {
  activeSlot: number;
  activeIndex: number;
  announcedIndex: number;
  repeats: number;
  middle: number;
  rootRef: React.RefObject<HTMLElement>;
  tabScrollRef: React.RefObject<HTMLDivElement>;
  viewportRef: React.RefObject<HTMLDivElement>;
  slotRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  step: (delta: -1 | 1) => void;
  selectCard: (cardId: string) => void;
  viewportProps: {
    onClick: (event: React.MouseEvent<HTMLDivElement>) => void;
    onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
    onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
    onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => void;
    onScroll: () => void;
    onTouchStart: () => void;
  };
};

/* The rail: its geometry, its four ways of moving the selection, and the
   teardown of every ref, timer and frame those gestures arm. */
export function useRailScroll(cardIds: string[]): RailScroll {
  const count = cardIds.length;
  /* One card cannot wrap, so it is not repeated — see REPEATS. */
  const repeats = count > 1 ? REPEATS : 1;
  const middle = count > 1 ? count : 0;

  /* THE ONLY SELECTION STATE IN THIS COMPONENT (AC16). */
  const [activeSlot, setActiveSlot] = useState(middle);
  const activeSlotRef = useRef(middle);
  /* Trails activeSlot: a scroll re-derives the grown card every frame, and
     announcing each one reads out positions the reader never landed on. */
  const [announcedSlot, setAnnouncedSlot] = useState(middle);

  const rootRef = useRef<HTMLElement>(null);
  const tabScrollRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const slotRefs = useRef<(HTMLDivElement | null)[]>([]);
  /* Rail pitch/origin/seek clamp, cleared by the resize effect and the layout
     effect that re-centres a re-authored list. Do not add a ResizeObserver. */
  const railRef = useRef<RailMetrics | null>(null);
  const seekTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const settleTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const scrollFrameRef = useRef(0);
  /* Own frame id — shares scrollFrameRef and the two would cancel each other. */
  const tabFrameRef = useRef(0);
  const glideRef = useRef(0);
  const glidePathRef = useRef<{ from: number; distance: number } | null>(null);
  const targetRef = useRef<number | null>(null);
  const dragRef = useRef<{ x: number; scrollLeft: number; active: boolean } | null>(null);
  /* A drag ends with a `click` on the capture target, so the gesture has to be
     able to say it was not a tap. */
  const draggedRef = useRef(false);

  /* Only a real user gesture flips this; a self-started seek must never
     re-derive its own trigger's selection. */
  const userScrollRef = useRef(false);

  /* Read through a ref, so the callbacks below keep one identity each. */
  const cardIdsRef = useRef(cardIds);
  cardIdsRef.current = cardIds;

  const activeIndex = count > 0 ? ((activeSlot % count) + count) % count : 0;
  const activeId = cardIds[activeIndex];

  /* Written, never rendered: holding it in state re-rendered the whole rail to
     set one attribute. The JSX keeps the default, so SSR still emits it. */
  useEffect(() => {
    const root = rootRef.current;
    if (root) {
      root.dataset.gccProfile = resolveGrowProfile(root);
    }
  }, []);

  const setSlot = useCallback((slot: number, announce = true) => {
    activeSlotRef.current = slot;
    setActiveSlot(slot);
    if (announce) {
      setAnnouncedSlot(slot);
    }
  }, []);

  /* A re-authored list re-pitches the strip under a stale slot index —
     re-anchored to the middle copy DURING RENDER so no paint shows the old
     state. */
  const countRef = useRef(count);
  if (countRef.current !== count) {
    countRef.current = count;
    setSlot(middle);
  }

  /* Read per call: a reader can turn the setting on with the page open. */
  const prefersReducedMotion = useCallback(
    () => typeof window?.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    []
  );

  /* Measured on first use. `centreOn` alone reads railRef directly and falls
     back when cold — warming it from a seek would answer for the wrong layout. */
  const railMetrics = useCallback((viewport: HTMLDivElement): RailMetrics | null => {
    if (!railRef.current) {
      railRef.current = measureRail(viewport, slotRefs.current);
    }
    return railRef.current;
  }, []);

  /* Slides the reader back into the middle copy without moving the picture.
     TWO pins, and they are not the same decision: the size swap is pinned only
     when the caller says the grow is cosmetic (`pinSizeSwap`, the equivalent
     slot in another copy being ungrown already), while the SHIFT pin is
     unconditional — see the write below. Declared before `glideTo` (temporal
     dead zone). */
  const rewrap = useCallback(
    (viewport: HTMLDivElement, pinSizeSwap = true) => {
      const cardCount = countRef.current;
      const slot = activeSlotRef.current;
      if (cardCount < 2 || (slot >= cardCount && slot < cardCount * 2)) {
        return;
      }

      const rail = railMetrics(viewport);
      if (!rail) {
        return;
      }

      const shift = slot < cardCount ? cardCount : -cardCount;
      const jump = shift * rail.pitch;
      const root = rootRef.current;
      /* WAT-11671: scroll position is read BEFORE the pin is written — do not
         reorder. Nothing below this line may read geometry, INCLUDING from an
         effect: the flushSync below commits synchronously and runs them here. */
      const landing = viewport.scrollLeft + jump;
      /* WAT-12630: UNCONDITIONAL, unlike the size pin below, and that asymmetry
         is the whole point. A rewrap teleports `scrollLeft`; the cards' own
         `transform` is now transitioned, so without this the shift glides for
         ~350ms across a rail that is not moving — measured at 81.66px with the
         card's width CONSTANT, i.e. entirely shift and not grow, worst frame
         52.23px. That is the WAT-12623 defect re-expressed. Before this diff the
         same displacement was a `justify-content` change, which is not
         animatable and so was always instant. Pinning `--gcc-grow-ms` here
         instead would be wrong: `step` passes `pinSizeSwap: false` precisely
         because a real user-initiated grow follows and must still animate. */
      root?.style.setProperty("--gcc-shift-ms", "0ms");
      if (pinSizeSwap) {
        root?.style.setProperty("--gcc-grow-ms", "0ms");
      }
      userScrollRef.current = false;
      /* WAT-12623: SYNCHRONOUS, so the post-wrap `data-side` and card size are
         in the DOM before the coordinate below lands — React alone splits them
         across frames and the one between paints the rail 111px off.
         `false`: a coordinate shift, never a change of grown card. */
      flushSync(() => setSlot(slot + shift, false));
      viewport.scrollLeft = landing;
      /* A glide can still be in flight; without this its next frame would
         undo the jump. */
      if (glidePathRef.current) {
        glidePathRef.current.from += jump;
      }
      /* Two frames: one to paint the swap, one before transitions resume. The
         shift pin is released on the same schedule it was written on — always —
         or every later `data-side` flip would snap instead of gliding. */
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          root?.style.removeProperty("--gcc-shift-ms");
          if (pinSizeSwap) {
            root?.style.removeProperty("--gcc-grow-ms");
          }
        });
      });
    },
    [railMetrics, setSlot]
  );

  /* One call: clearing the timer and the target apart is how one survives. */
  const endSeek = useCallback(() => {
    clearTimeout(seekTimerRef.current);
    targetRef.current = null;
  }, []);

  /* Drives the scroll ourselves (native `behavior: "smooth"` is not tunable),
     on the CSS's own curve. Both exits rewrap — scrollend alone misses a
     re-selected active tab. Returns whether a glide started. */
  const glideTo = useCallback((
    viewport: HTMLDivElement,
    target: number
  ): boolean => {
    cancelAnimationFrame(glideRef.current);
    const distance = target - viewport.scrollLeft;
    if (Math.abs(distance) < 1) {
      glidePathRef.current = null;
      /* Cancels this glide's own seek window too. */
      endSeek();
      /* Synchronous, and `false` because a grow may well be IN FLIGHT — the
         opposite of "nothing to hide". This exit fires when the rail is already
         within 1px of its target, which is exactly what a click, step or
         `selectCard` leaves behind after its own `setSlot`: there is a real
         grow to let run, and pinning the size here would snap it. The SHIFT pin
         is not a caller's choice and `rewrap` writes it regardless
         (WAT-12630). */
      rewrap(viewport, false);
      return false;
    }

    const duration = Math.min(GLIDE_MAX_MS, GLIDE_MIN_MS + Math.abs(distance) * 0.22);
    const started = performance.now();

    /* Ref, not closured — a wrap landing mid-flight must not be undone by a
       stale origin. */
    const path = {
      from: viewport.scrollLeft,
      distance
    };
    glidePathRef.current = path;

    const frame = (now: number) => {
      const progress = Math.min(1, (now - started) / duration);
      viewport.scrollLeft = path.from + path.distance * easeGlide(progress);
      if (progress < 1) {
        glideRef.current = requestAnimationFrame(frame);
      } else {
        glideRef.current = 0;
        glidePathRef.current = null;
        /* Ends the seek before rewrap, so the jump doesn't read as overshoot
           to scrollend. */
        endSeek();
        rewrap(viewport);
      }
    };
    glideRef.current = requestAnimationFrame(frame);
    return true;
  }, [endSeek, rewrap]);

  const centreOn = useCallback(
    (slot: number, behavior: "smooth" | "auto" = "smooth") => {
      const viewport = viewportRef.current;
      const node = slotRefs.current[slot];
      if (!viewport || !node) {
        return;
      }

      /* Arithmetic wherever the rail is measured — cheaper than the
         rect-based fallback. */
      const rail = railRef.current;
      const target = rail ? seekTarget(rail, slot) : measuredSeekTarget(viewport, node);

      userScrollRef.current = false;

      if (behavior === "auto" || prefersReducedMotion()) {
        cancelAnimationFrame(glideRef.current);
        glidePathRef.current = null;
        endSeek();
        viewport.scrollLeft = target;
        /* Jump, then rewrap immediately (AC31). PINNED BY DEFAULT — no explicit
           argument, where HEAD passed `false` (WAT-12630). `behavior: "auto"`
           has exactly ONE caller, the resize handler below, and it re-centres
           `activeSlotRef.current`: nothing the reader chose changes, so the only
           size change on this path is `rewrap`'s own cosmetic hand-off of
           "grown" to the equivalent slot in another copy — precisely what
           `pinSizeSwap` exists to hide. Unpinned it ANIMATED that hand-off:
           after the teleport the on-screen card is the DOM node that was
           ungrown, so it reads 534 and grows back to 645 over 520ms. Reachable
           whenever a resize lands with the active slot outside the middle copy,
           which is the normal state while arrow-stepping.
           ⚠ Under reduced motion the argument is MOOT either way — foundation.css
           collapses every transition on the tree to 0.01ms, so pinned and
           unpinned are indistinguishable there. This arm is also where the shift
           drift was measured, which the unconditional pin inside `rewrap` — not
           this argument — is what stops. */
        rewrap(viewport);
        return;
      }

      /* WAT-11671: armed only once a glide actually starts, or targetRef
         could gate a native scroll for up to SEEK_MS. */
      if (!glideTo(viewport, target)) {
        return;
      }

      targetRef.current = target;
      clearTimeout(seekTimerRef.current);
      seekTimerRef.current = setTimeout(() => {
        targetRef.current = null;
      }, SEEK_MS);
    },
    [endSeek, glideTo, prefersReducedMotion, rewrap]
  );

  /* Land on a card, then rewrap. */
  const scheduleSettle = useCallback(
    (viewport: HTMLDivElement) => {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = setTimeout(() => {
        if (dragRef.current) {
          return;
        }
        const rail = railMetrics(viewport);
        const slot = nearestSlot(viewport, slotRefs.current, rail);
        setSlot(slot);
        /* A slot with no node has been shed; nothing to centre on. */
        const node = slotRefs.current[slot];
        if (!node) {
          rewrap(viewport);
          return;
        }
        /* Same off-centre test centreOn uses, so the two agree. */
        const offCentre = rail ?
          rail.base + slot * rail.pitch - viewport.scrollLeft :
          distanceFromCentre(viewport, node);
        if (Math.abs(offCentre) > 2) {
          centreOn(slot);
        } else {
          rewrap(viewport);
        }
      }, SETTLE_MS);
    },
    [centreOn, railMetrics, rewrap, setSlot]
  );

  /* Step by whole slots. Wrapping means there is always a neighbour, so the
     arrows never disable (AC17). */
  const step = useCallback((delta: -1 | 1) => {
    const viewport = viewportRef.current;
    const cardCount = countRef.current;
    const extent = cardCount * (cardCount > 1 ? REPEATS : 1);
    if (cardCount < 2 || !viewport) {
      return;
    }

    /* Rewrap first rather than refuse the step — keeps the arrows always
       enabled (AC17). `false` leaves the SIZE swap animated, because the grow
       moves for real here: a user-initiated step follows immediately.
       ⚠ The shift pin inside `rewrap` does NOT cover only the teleport, and
       cannot. `setSlot`/`centreOn` below run in the SAME tick and React flushes
       a discrete-event update before any rAF, so the reader's own `data-side`
       flip lands inside the pin window too and snaps while width glides. One
       style recalc sees only the NET transform delta and cannot separate
       teleport-induced from user-induced, so pinning both is the only
       implementable choice — the alternative is the measured 81.66px glide
       across a motionless rail (WAT-12630). */
    if (activeSlotRef.current + delta < 0 || activeSlotRef.current + delta >= extent) {
      rewrap(viewport, false);
    }

    const next = activeSlotRef.current + delta;
    if (next < 0 || next >= extent) {
      return;
    }
    setSlot(next);
    centreOn(next);
  }, [centreOn, rewrap, setSlot]);

  /* Home/End/tab name a CARD, not a slot — seek whichever copy is nearest. */
  const selectCard = useCallback((cardId: string) => {
    const ids = cardIdsRef.current;
    const realIndex = ids.indexOf(cardId);
    if (realIndex < 0) {
      return;
    }

    const copies = ids.length > 1 ? REPEATS : 1;
    const from = activeSlotRef.current;
    let best = from;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (let copy = 0; copy < copies; copy += 1) {
      const candidate = copy * ids.length + realIndex;
      const distance = Math.abs(candidate - from);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = candidate;
      }
    }
    setSlot(best);
    centreOn(best);
  }, [centreOn, setSlot]);

  /* Keeps the selected tab in view: `scroll="host"` hands that duty to the
     host (WCAG 2.4.11). Scrolls the tray only, never the page. */
  useEffect(() => {
    const scroller = tabScrollRef.current;
    if (!scroller || activeId === undefined) {
      return undefined;
    }

    /* WAT-11671: two frames, not one — a rAF callback runs BEFORE that
       frame's own style/layout pass. */
    tabFrameRef.current = requestAnimationFrame(() => {
      tabFrameRef.current = requestAnimationFrame(() => {
        tabFrameRef.current = 0;

        if (scroller.scrollWidth <= scroller.clientWidth) {
          return;
        }

        /* Matched by value rather than by an attribute selector, so a card id
           needing escaping cannot break the query. */
        const inputs = scroller.querySelectorAll<HTMLInputElement>("input[type=radio]");
        const input = Array.from(inputs).find(candidate => candidate.value === activeId);
        const pill = input?.closest("span");
        if (!pill) {
          return;
        }

        const edge = scroller.getBoundingClientRect();
        const box = pill.getBoundingClientRect();
        let delta = 0;
        if (box.left < edge.left + TAB_GUTTER_PX) {
          delta = box.left - edge.left - TAB_GUTTER_PX;
        } else if (box.right > edge.right - TAB_GUTTER_PX) {
          delta = box.right - edge.right + TAB_GUTTER_PX;
        }

        if (delta !== 0) {
          scroller.scrollBy({
            left: delta,
            behavior: prefersReducedMotion() ? "auto" : "smooth"
          });
        }
      });
    });

    return () => cancelAnimationFrame(tabFrameRef.current);
  }, [activeId, prefersReducedMotion]);

  /* Centre the middle copy before first paint — isomorphic because the shell
     is "use client" and therefore still SERVER-RENDERED. */
  useIsomorphicLayoutEffect(() => {
    /* A shorter authored list leaves detached nodes past the new end. */
    slotRefs.current.length = count * repeats;
    /* A re-authored list changes the strip extent; maxScroll must be
       re-measured. */
    railRef.current = null;

    const viewport = viewportRef.current;
    const node = slotRefs.current[middle];
    if (!viewport || !node) {
      return;
    }
    viewport.scrollLeft += distanceFromCentre(viewport, node);
  }, [count, middle, repeats]);

  /* A breakpoint change re-pitches the slots under a scrollLeft measured for
     the old pitch, so the grown card is put back under the reader. */
  useEffect(() => {
    const onResize = () => {
      /* --gcc-slot and the track gap both step at breakpoints; the cached
         pitch is stale. */
      railRef.current = null;
      centreOn(activeSlotRef.current, "auto");
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [centreOn]);

  /* Horizontal wheel only — a vertical wheel belongs to the page.
     `shiftKey` counts as horizontal: Chrome reports shift+wheel on deltaY. */
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    const onWheel = (event: WheelEvent) => {
      if (!event.shiftKey && Math.abs(event.deltaX) <= Math.abs(event.deltaY)) {
        return;
      }
      userScrollRef.current = true;
      scheduleSettle(viewport);
    };

    viewport.addEventListener("wheel", onWheel, { passive: true });
    return () => viewport.removeEventListener("wheel", onWheel);
  }, [scheduleSettle]);

  /* The reader's own scrolls rewrap here; a seek we started rewraps in
     `glideTo`. `scrollend` fires at intermediate rests in Chrome, so this reads
     no geometry and stays idempotent however often it runs. */
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || !("onscrollend" in viewport)) {
      return;
    }

    const onScrollEnd = () => {
      if (targetRef.current !== null && Math.abs(viewport.scrollLeft - targetRef.current) > 2) {
        return;
      }

      endSeek();
      if (!dragRef.current) {
        userScrollRef.current = false;
        rewrap(viewport);
      }
    };

    viewport.addEventListener("scrollend", onScrollEnd);
    return () => viewport.removeEventListener("scrollend", onScrollEnd);
  }, [endSeek, rewrap]);

  useEffect(
    () => () => {
      clearTimeout(seekTimerRef.current);
      clearTimeout(settleTimerRef.current);
      cancelAnimationFrame(scrollFrameRef.current);
      cancelAnimationFrame(tabFrameRef.current);
      cancelAnimationFrame(glideRef.current);
    },
    []
  );

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    /* Cleared before the touch branch below, so a tap after a mouse drag is
       not swallowed by the previous gesture's suppression. */
    draggedRef.current = false;

    if (event.pointerType === "touch") {
      return;
    }

    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    clearTimeout(settleTimerRef.current);
    dragRef.current = {
      x: event.clientX,
      scrollLeft: viewport.scrollLeft,
      active: false
    };
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const viewport = viewportRef.current;
    const drag = dragRef.current;
    if (!viewport || !drag) {
      return;
    }

    const travelled = event.clientX - drag.x;
    if (!drag.active) {
      if (Math.abs(travelled) < DRAG_THRESHOLD_PX) {
        return;
      }
      drag.active = true;
      draggedRef.current = true;

      cancelAnimationFrame(glideRef.current);
      glideRef.current = 0;
      glidePathRef.current = null;
      userScrollRef.current = true;
      clearTimeout(seekTimerRef.current);
      targetRef.current = null;

      viewport.setPointerCapture(event.pointerId);
      viewport.dataset.dragging = "true";
    }

    viewport.scrollLeft = drag.scrollLeft - travelled;
  };

  const endDrag = useCallback((pointerId: number) => {
    const viewport = viewportRef.current;
    const drag = dragRef.current;
    dragRef.current = null;
    if (!viewport || !drag) {
      return;
    }

    delete viewport.dataset.dragging;
    if (drag.active && viewport.hasPointerCapture(pointerId)) {
      viewport.releasePointerCapture(pointerId);
    }

    if (drag.active) {
      scheduleSettle(viewport);
    }
  }, [scheduleSettle]);

  /* WAT-11671: released on the window, not the viewport — a press that lifts
     outside otherwise leaves the drag armed, and the next hover reads as a
     drag and swallows its click. */
  useEffect(() => {
    const release = (event: PointerEvent) => endDrag(event.pointerId);
    window.addEventListener("pointerup", release);
    window.addEventListener("pointercancel", release);
    return () => {
      window.removeEventListener("pointerup", release);
      window.removeEventListener("pointercancel", release);
    };
  }, [endDrag]);

  /* Click-to-grow (AC15): one handler on the scroller, not per slot — a card
     holds its own controls, and a control inside a control is invalid. */
  const onClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (draggedRef.current) {
      return;
    }

    /* A press that landed on a real control belongs to that control. */
    if ((event.target as HTMLElement)?.closest?.("a, button, input, label, summary")) {
      return;
    }

    const slot = slotAt(slotRefs.current, event.clientX);
    if (slot === null || slot === activeSlotRef.current) {
      return;
    }

    setSlot(slot);
    centreOn(slot);
  };

  const onScroll = () => {
    if (!userScrollRef.current || scrollFrameRef.current) {
      return;
    }

    scrollFrameRef.current = requestAnimationFrame(() => {
      scrollFrameRef.current = 0;
      const viewport = viewportRef.current;
      if (!viewport) {
        return;
      }
      setSlot(nearestSlot(viewport, slotRefs.current, railMetrics(viewport)), false);
      scheduleSettle(viewport);
    });
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    /* THE RAIL'S OWN KEYS ONLY — a keydown bubbling out of a card's CTA must
       not seek, or focus falls to `<body>` (WCAG 2.4.3/2.4.7). The final block
       below must still fire for a bubbled key. */
    const fromRail = event.target === event.currentTarget;

    if (fromRail && event.key === "ArrowRight") {
      event.preventDefault();
      step(1);
      return;
    }

    if (fromRail && event.key === "ArrowLeft") {
      event.preventDefault();
      step(-1);
      return;
    }

    /* Home/End name a CARD; on a wrapping rail the nearest copy of the first
       card can be forward of the reader — that's correct, not a bug. */
    if (fromRail && (event.key === "Home" || event.key === "End")) {
      event.preventDefault();
      const ids = cardIdsRef.current;
      selectCard(event.key === "Home" ? ids[0] : ids[ids.length - 1]);
      return;
    }

    /* Catches keys this handler doesn't claim (PageUp/Down, Space, …) that can
       still scroll the rail natively. Only while nothing we started is in
       flight — a keystroke mid-glide must not steal the glide's own selection. */
    if (glidePathRef.current === null && targetRef.current === null) {
      userScrollRef.current = true;
    }
  };

  return {
    activeIndex,
    activeSlot,
    announcedIndex: count > 0 ? ((announcedSlot % count) + count) % count : 0,
    middle,
    repeats,
    rootRef,
    selectCard,
    slotRefs,
    step,
    tabScrollRef,
    viewportProps: {
      onClick,
      onKeyDown,
      onPointerDown,
      onPointerMove,
      onScroll,
      onTouchStart: () => {
        userScrollRef.current = true;
      }
    },
    viewportRef
  };
}
